import React, { useState, useMemo } from "react";
import {
   Card,
   Box,
   EmptyState,
   FlexList,
   Spinner,
   Typography,
   useI18nContext,
   DateSelect,
   Select,
} from "@procore/core-react";
import { LaborGraph } from "../labor-graph/labor-graph";
import { useGetCompanyTotalsBreakdownQuery } from "./queries";
import {
   RANGE_OPTIONS,
   DEFAULT_RANGE_DAYS,
   inferBreakdown,
   processCompanyTotalsForGraph,
} from "./helpers";
import { ResourceBreakdownTable } from "./resource-breakdown-table";

export const Totals = () => {
   const I18n = useI18nContext();

   const [startDate, setStartDate] = useState<Date>(new Date());
   const [rangeDays, setRangeDays] = useState<number>(DEFAULT_RANGE_DAYS);

   const breakdown = useMemo(() => inferBreakdown(rangeDays), [rangeDays]);

   const startDayISO = useMemo(() => startDate.toISOString().split("T")[0], [startDate]);
   const endDayISO = useMemo(() => {
      const end = new Date(startDate);
      end.setDate(end.getDate() + rangeDays);
      return end.toISOString().split("T")[0];
   }, [startDate, rangeDays]);

   const {
      data: companyTotalsData,
      isLoading,
      isFetching,
   } = useGetCompanyTotalsBreakdownQuery(startDayISO, endDayISO, breakdown);

   const graphData = useMemo(() => {
      if (!companyTotalsData) return [];
      return processCompanyTotalsForGraph(companyTotalsData, "both", I18n);
   }, [companyTotalsData, I18n]);

   const assignablePlotLines = useMemo(() => {
      if (!companyTotalsData) return undefined;
      const totalAvailable = companyTotalsData.total_people + companyTotalsData.total_equipment;
      if (!totalAvailable) return undefined;
      return [
         {
            value: totalAvailable,
            color: "#E74C3C",
            dashStyle: "Dash" as const,
            width: 2,
            label: {
               text: I18n.t(
                  `views.company.workforce_planning.totals_page.total_assignable_resources`,
               ),
               align: "right" as const,
               style: { color: "#E74C3C", fontWeight: "bold" },
            },
            zIndex: 5,
         },
      ];
   }, [companyTotalsData, I18n]);

   const selectedRangeOption = RANGE_OPTIONS.find((opt) => opt.days === rangeDays);
   const selectedRangeLabel = selectedRangeOption ? I18n.t(selectedRangeOption.translationKey) : "";

   const hasData = graphData.length > 0;

   const xAxisMax = useMemo(() => {
      const end = new Date(startDate);
      end.setDate(end.getDate() + rangeDays);
      return end.getTime();
   }, [startDate, rangeDays]);

   return (
      <Box padding="lg">
         <Card>
            <Box padding="md">
               <FlexList space="md" alignItems="center">
                  <Typography intent="h3">
                     {I18n.t(`views.company.workforce_planning.totals_page.totals_by`)}
                  </Typography>
                  <Select
                     label={I18n.t(`views.company.workforce_planning.totals_page.view_company`)}
                     onSelect={() => {}}
                     aria-label={I18n.t(`views.company.workforce_planning.totals_page.view`)}
                  >
                     <Select.Option value="company" selected>
                        {I18n.t(`views.company.workforce_planning.totals_page.view_company`)}
                     </Select.Option>
                  </Select>
                  <Select
                     label={I18n.t(`views.company.workforce_planning.totals_page.metric_resources`)}
                     onSelect={() => {}}
                     aria-label={I18n.t(`views.company.workforce_planning.totals_page.metric`)}
                  >
                     <Select.Option value="resources" selected>
                        {I18n.t(`views.company.workforce_planning.totals_page.metric_resources`)}
                     </Select.Option>
                  </Select>
                  <DateSelect
                     onChange={(newDate) => setStartDate(newDate ?? new Date())}
                     value={startDate}
                     aria-label={I18n.t(`views.company.workforce_planning.totals_page.start_date`)}
                  />
                  <Select
                     label={selectedRangeLabel}
                     onSelect={({ item }: { item: number }) => setRangeDays(item)}
                     aria-label={I18n.t(`views.company.workforce_planning.totals_page.range`)}
                     style={{ width: "140px" }}
                  >
                     {RANGE_OPTIONS.map((option) => (
                        <Select.Option
                           key={option.days}
                           value={option.days}
                           selected={option.days === rangeDays}
                        >
                           {I18n.t(option.translationKey)}
                        </Select.Option>
                     ))}
                  </Select>
               </FlexList>
            </Box>
            <Spinner loading={isLoading || isFetching}>
               {hasData ? (
                  <Box padding="md">
                     <LaborGraph
                        data={graphData}
                        graphStart={startDate.getTime()}
                        height="400px"
                        enableZoom={true}
                        yAxisPlotLines={assignablePlotLines}
                        xAxisMin={startDate.getTime()}
                        xAxisMax={xAxisMax}
                        xAxisDateOnly={true}
                     />
                  </Box>
               ) : (
                  <Box padding="lg">
                     <EmptyState compact>
                        <EmptyState.NoItems />
                        <EmptyState.Title>
                           {I18n.t(
                              `views.company.workforce_planning.totals_page.empty_state_title`,
                           )}
                        </EmptyState.Title>
                        <EmptyState.Description>
                           {I18n.t(
                              `views.company.workforce_planning.totals_page.empty_state_description`,
                           )}
                        </EmptyState.Description>
                     </EmptyState>
                  </Box>
               )}
            </Spinner>
         </Card>

         <Box paddingTop="md">
            <ResourceBreakdownTable
               data={companyTotalsData}
               breakdown={breakdown}
               loading={isLoading}
            />
         </Box>
      </Box>
   );
};
